/* Pure snapshot-based recommendation engine. See ../data/ketcherguide-scoring.md. */
(function (root, factory) {
  const engine = factory();
  if (typeof module === 'object' && module.exports) module.exports = engine;
  if (root) root.KetcherGuide = engine;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  const OPTIONS = Object.freeze({
    audience: ['junior', 'beginner', 'club'],
    style: ['attack', 'speed', 'allround'],
    weight: ['light', 'heavy', 'any'],
    flex: ['flexible', 'stiff', 'any'],
    budget: ['under500', 'mid', 'over1000', 'any']
  });
  const WEIGHTS = Object.freeze({balance: 4, producerStyle: 2, weight: 3, flex: 3, mediumFlex: 1, budget: 8});
  // AAshop 15/10-2026: engelsk visning på /en-eu og /en-se (html lang="en"). Kun viste tekster; logik, nøgler og data er uændrede.
  const AA_EN = !/^da\b/i.test((typeof document !== 'undefined' && document.documentElement && document.documentElement.lang) || 'da');
  const tx = (da, en) => (AA_EN ? en : da);
  const EN_VALUES = {
    lige: 'even', hovedtung: 'head-heavy', hovedlet: 'head-light',
    stiv: 'stiff', medium: 'medium', fleksibel: 'flexible',
    'angreb/kraft': 'attack/power', kontrol: 'control', 'hurtighed/manøvredygtighed': 'speed/manoeuvrability', hurtighed: 'speed',
    allround: 'all-round', 'allround/kontrol': 'all-round/control', 'kontrol og kraft': 'control and power',
    junior: 'junior', 'begynder/fritid': 'beginner/recreational', 'begynder/øvet': 'beginner/intermediate', avanceret: 'advanced',
    fritid: 'recreational', 'junior/fritid': 'junior/recreational', 'øvet': 'intermediate', 'øvet/avanceret': 'intermediate/advanced',
    'øvet (erfaren)': 'intermediate (experienced)'
  };
  const shown = value => {
    if (!AA_EN || typeof value !== 'string') return value;
    if (Object.prototype.hasOwnProperty.call(EN_VALUES, value)) return EN_VALUES[value];
    return value.replace(/ eller /g, ' or ').replace('variant ikke angivet hos AAshop', 'variant not specified at AAshop').replace(/\bca\. /g, 'approx. ');
  };
  const FIELD_LABELS = AA_EN
    ? {balance: 'Balance', vaegt: 'Weight', stivhed: 'Stiffness', spillestil: 'Playing style', niveau: 'Target group'}
    : {balance: 'Balance', vaegt: 'Vægt', stivhed: 'Stivhed', spillestil: 'Spillestil', niveau: 'Målgruppe'};
  const TARGET_BALANCE = {attack: 'hovedtung', speed: 'hovedlet', allround: 'lige'};
  const TARGET_FLEX = {flexible: 'fleksibel', stiff: 'stiv'};

  function validateAnswers(answers) {
    for (const [key, values] of Object.entries(OPTIONS)) {
      if (!answers || !values.includes(answers[key])) throw new Error('Ugyldigt eller manglende svar: ' + key);
    }
  }

  function inBudget(price, choice) {
    if (choice === 'under500') return price <= 500;
    if (choice === 'mid') return price >= 500 && price <= 1000;
    if (choice === 'over1000') return price > 1000;
    return true;
  }

  function budgetDistance(price, choice) {
    if (inBudget(price, choice)) return 0;
    if (choice === 'under500') return price - 500;
    if (choice === 'mid') return price < 500 ? 500 - price : price - 1000;
    return 1000 - price;
  }

  function hasSource(product, field) {
    const source = product.sources && product.sources[field];
    return Boolean(source && /^https?:\/\//.test(source.url) && ['producent', 'forhandler'].includes(source.type));
  }

  function beginnerOnly(level) {
    return Boolean(level && /begynder|fritid/i.test(level) && !/øvet|avanceret/i.test(level));
  }

  /** Hard boundaries are never relaxed, including when no full preference match exists. */
  function exclusion(product, answers) {
    if (product.kan_koebes !== true) return 'Varen kan ikke købes i snapshot.';
    if (product.excluded || !['junior', 'ketcher'].includes(product.category) || /^fz-forza-game-/.test(product.handle)) {
      return product.exclusion_reason || 'Varen er udelukket i briefen.';
    }
    if (!product.image) return 'Billede mangler i det godkendte snapshot.';
    if (!Number.isFinite(product.price) || product.price < 0) return 'Pris mangler i snapshot.';
    if (answers.audience === 'junior' && product.category !== 'junior') return 'Kun juniorketchere til junior.';
    if (answers.audience !== 'junior' && product.category !== 'ketcher') return 'Juniorketchere er udelukket til voksne.';
    if (answers.audience === 'beginner' && (!/begynder|fritid/i.test(product.niveau || '') || !hasSource(product, 'niveau'))) return 'Begyndervalg kræver en dokumenteret begynder- eller fritidsmålgruppe.';
    if (answers.audience === 'club' && beginnerOnly(product.niveau)) return 'Dokumenteret til begynder/fritid.';
    if (!['hovedlet', 'lige', 'hovedtung'].includes(product.balance) || !hasSource(product, 'balance')) {
      return 'Alle stilvalg afhænger af en dokumenteret balance.';
    }
    if (answers.weight !== 'any' && (!product.weight_group || !hasSource(product, 'vaegt'))) {
      return 'Vægtønsket kan ikke vurderes for den dokumenterede variant.';
    }
    if (answers.flex !== 'any' && (!product.flex || !hasSource(product, 'stivhed'))) {
      return 'Skaftønsket kræver dokumenteret stivhed.';
    }
    return null;
  }

  function producerStyleMatches(style, answer) {
    if (!style) return false;
    if (answer === 'attack') return /angreb|kraft/.test(style);
    if (answer === 'speed') return /hurtighed|manøvredygtighed/.test(style);
    return /allround|kontrol/.test(style);
  }

  function money(number) {
    return new Intl.NumberFormat('da-DK', {maximumFractionDigits: 2}).format(number) + ' kr.';
  }

  /**
   * Documented points per answer: balance 4 (nearest 2 for even balance),
   * producer style 2, weight 3, flex 3 (medium 1), budget 8. No audience points.
   * Unselected weight/flex/budget add zero points. Missing information earns zero.
   * Null means that a hard rule or required data prevents a recommendation.
   */
  function scoreProduct(product, answers) {
    validateAnswers(answers);
    if (exclusion(product, answers)) return null;
    let score = 0;
    let exact = true;
    const reasons = [];
    const differences = [];
    const missing = [];
    const breakdown = {balance: 0, producerStyle: 0, weight: 0, flex: 0, budget: 0};

    if (answers.audience === 'junior') reasons.push(tx('En juniorketcher ifølge producentens oplysninger.', 'A junior racket according to the manufacturer.'));
    if (answers.audience === 'beginner') reasons.push(tx('Producentens målgruppe er ', 'The manufacturer\'s target group is ') + shown(product.niveau) + '.');
    if (product.balance === TARGET_BALANCE[answers.style]) {
      breakdown.balance = WEIGHTS.balance;
      const texts = AA_EN ? {
        attack: 'A head-heavy balance matches your wish for attack and smashes.',
        speed: 'A head-light balance matches your wish for fast play and defence.',
        allround: 'An even balance matches your wish for a bit of everything.'
      } : {
        attack: 'Hovedtung balance matcher dit ønske om angreb og smash.',
        speed: 'Hovedlet balance matcher dit ønske om hurtigt spil og forsvar.',
        allround: 'Lige balance matcher dit ønske om lidt af det hele.'
      };
      reasons.push(texts[answers.style]);
    } else {
      exact = false;
      breakdown.balance = product.balance === 'lige' ? 2 : (answers.style === 'allround' ? 1 : 0);
      const targets = AA_EN ? {attack: 'a head-heavy balance', speed: 'a head-light balance', allround: 'an even balance'} : {attack: 'hovedtung balance', speed: 'hovedlet balance', allround: 'lige balance'};
      const actual = AA_EN ? {lige: 'Even balance', hovedlet: 'Head-light balance', hovedtung: 'Head-heavy balance'} : {lige: 'Lige balance', hovedlet: 'Hovedlet balance', hovedtung: 'Hovedtung balance'};
      differences.push(actual[product.balance] + tx(', mens dit stilvalg peger mod ', ', while your style choice points to ') + targets[answers.style] + '.');
    }

    if (product.style && hasSource(product, 'spillestil') && producerStyleMatches(product.style, answers.style)) {
      breakdown.producerStyle = WEIGHTS.producerStyle;
      reasons.push(tx('Producentens spillestil: ', 'The manufacturer\'s playing style: ') + shown(product.style) + '.');
    } else if (product.style && hasSource(product, 'spillestil')) {
      exact = false;
      const choices = AA_EN ? {attack: 'attack and smashes', speed: 'fast play and defence', allround: 'a bit of everything'} : {attack: 'angreb og smash', speed: 'hurtigt spil og forsvar', allround: 'lidt af det hele'};
      differences.push(tx('Producentens spillestil er ', 'The manufacturer\'s playing style is ') + shown(product.style) + tx(', mens du har valgt ', ', while you chose ') + choices[answers.style] + '.');
    }
    if (!product.style || !hasSource(product, 'spillestil')) {
      missing.push(tx('Spillestil er ikke dokumenteret; stilvalget vurderes kun ud fra balance.', 'Playing style is not documented, so your style choice is judged on balance only.'));
    }

    if (answers.weight !== 'any') {
      if (product.weight_group === answers.weight) {
        breakdown.weight = WEIGHTS.weight;
        reasons.push((answers.weight === 'light' ? tx('Let vægtgruppe: ', 'Light weight class: ') : tx('Vægtgruppe med mere tyngde: ', 'Heavier weight class: ')) + shown(product.weight) + '.');
      } else {
        exact = false;
        differences.push(tx('Vægten er ', 'The weight is ') + shown(product.weight) + tx(', som ligger i ', ', which is in ') + (product.weight_group === 'light' ? tx('den lette gruppe', 'the light class') : tx('gruppen med mere tyngde', 'the heavier class')) + '.');
      }
    }
    if (!product.weight) missing.push(tx('Vægt er ikke dokumenteret og indgår ikke i vurderingen.', 'Weight is not documented and is not part of the assessment.'));
    if (product.ambiguous_weight) {
      missing.push(tx('Den præcise vægtvariant hos AAshop er ikke oplyst. Kilden angiver ', 'The exact weight variant at AAshop is not stated. The source lists ') + product.weight_classes.join(tx(' eller ', ' or ')) + '.');
    }

    if (answers.flex !== 'any') {
      if (product.flex === TARGET_FLEX[answers.flex]) {
        breakdown.flex = WEIGHTS.flex;
        reasons.push(AA_EN ? (product.flex === 'stiv' ? 'A stiff' : 'A flexible') + ' shaft matches your choice.' : (product.flex === 'stiv' ? 'Stift' : 'Fleksibelt') + ' skaft matcher dit valg.');
      } else {
        exact = false;
        breakdown.flex = product.flex === 'medium' ? WEIGHTS.mediumFlex : 0;
        differences.push(tx('Stivheden er ', 'The stiffness is ') + shown(product.flex) + tx('; du valgte ', ', and you chose ') + shown(TARGET_FLEX[answers.flex]) + '.');
      }
    }
    if (!product.flex) missing.push(tx('Stivhed er ikke dokumenteret og indgår ikke i vurderingen.', 'Stiffness is not documented and is not part of the assessment.'));
    if (!product.niveau) missing.push(tx('Niveau er ikke dokumenteret. Der skelnes ikke mellem klub og elite.', 'Level is not documented. No distinction is made between club and elite.'));

    if (answers.budget !== 'any') {
      if (inBudget(product.price, answers.budget)) {
        breakdown.budget = WEIGHTS.budget;
        reasons.push(tx('Prisen ligger i dit valgte budget.', 'The price is within your chosen budget.'));
      } else {
        exact = false;
        const ranges = AA_EN ? {under500: 'up to DKK 500', mid: 'DKK 500 to 1,000', over1000: 'over DKK 1,000'} : {under500: 'op til 500 kr.', mid: '500 til 1.000 kr.', over1000: 'over 1.000 kr.'};
        differences.push(tx('Prisen er ', 'The price is ') + money(product.price) + tx(', uden for dit valgte interval ', ', outside your chosen range of ') + ranges[answers.budget] + '.');
      }
    }

    score = Object.values(breakdown).reduce((sum, value) => sum + value, 0);
    const sources = Object.entries(product.sources || {})
      .filter(([field]) => FIELD_LABELS[field] && hasSource(product, field))
      .map(([field, source]) => ({field, label: FIELD_LABELS[field], url: source.url, type: source.type}));
    const sourceTypes = new Set(sources.map(source => source.type));
    const sourceSummary = sourceTypes.has('forhandler')
      ? tx('Anbefalingen bygger på dokumenterede oplysninger fra producent og forhandler om balance, vægt og stivhed.', 'The suggestion is based on documented information from the manufacturer and retailers about balance, weight and stiffness.')
      : tx('Anbefalingen bygger på producentens oplysninger om balance, vægt og stivhed.', 'The suggestion is based on the manufacturer\'s information about balance, weight and stiffness.');
    return {product, score, breakdown, exact, reasons, differences, missing, sources, sourceSummary};
  }

  /** No random tie-breaks and no writes. The caller owns the source objects. */
  function recommend(products, answers) {
    validateAnswers(answers);
    const ranked = products.map(product => scoreProduct(product, answers)).filter(Boolean).sort((a, b) =>
      Number(b.exact) - Number(a.exact) || b.score - a.score ||
      budgetDistance(a.product.price, answers.budget) - budgetDistance(b.product.price, answers.budget) ||
      a.product.price - b.product.price || a.product.handle.localeCompare(b.product.handle, 'da')
    );
    const seen = new Set();
    const distinct = ranked.filter(item => {
      // Only colors sharing both documented model and identical weight can collapse.
      const key = item.product.model + '|' + item.product.weight;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const recommendations = distinct.slice(0, 3);
    const exactCount = distinct.filter(item => item.exact).length;
    const notices = [];
    if (!recommendations.length) notices.push(tx('Der er ingen dokumenterede muligheder i denne gruppe i snapshottet. Se alle ketchere og få hjælp til dit valg.', 'There are no documented options in this group in the range. See all rackets and get help with your choice.'));
    else if (!exactCount) notices.push(tx('Ingen ketcher matcher alle dine valg. Her er de nærmeste dokumenterede muligheder, og du kan se afvigelserne på hvert kort.', 'No racket matches all your choices. Here are the closest documented options, and each card shows how it differs.'));
    else if (recommendations.some(item => !item.exact)) notices.push(tx('Nogle forslag er nærmeste match. Afvigelser fra dine valg fremgår på kortene.', 'Some suggestions are the closest match. Differences from your choices are shown on the cards.'));
    if (answers.audience === 'junior' && recommendations.length && distinct.length < 3) {
      const count = recommendations.length;
      notices.push(AA_EN
        ? (count === 1 ? 'There is only 1 junior racket' : 'There are only ' + count + ' junior rackets') + ' with the required information in the range. That is why we show ' + count + (count === 1 ? ' suggestion.' : ' suggestions.')
        : 'Der er kun ' + count + (count === 1 ? ' juniorketcher' : ' juniorketchere') + ' med de nødvendige oplysninger i udvalget. Derfor viser vi ' + count + ' forslag.');
    }
    return {recommendations, exactCount, eligibleCount: distinct.length, notice: notices.join(' ')};
  }

  return Object.freeze({OPTIONS, WEIGHTS, inBudget, budgetDistance, beginnerOnly, exclusion, scoreProduct, recommend});
});
